import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  CircularProgress, Divider, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'i18n/format';
import { useTokens } from 'theme';
import useAuthStore from 'stores/authStore';
import useRole from 'hooks/useRole';
import { extractApiError } from 'api/errors';
import {
  useApiKeys, useBiConnections, useBiConnectionsAccess, useBiUsage,
  useDeleteApiKey, useQueryLogs, useRevokeBiConnection,
} from 'api/bi';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import { fmtDate, fmtRelative } from 'pages/console/bi-api/format';
import RecentQueries from 'pages/console/bi-api/RecentQueries';
import StatCards from 'pages/console/bi-api/StatCards';
import TokensPanel from 'pages/console/bi-api/TokensPanel';
import UsageTrend from 'pages/console/bi-api/UsageTrend';
import ConnectionInstructions from './ConnectionInstructions';
import ConnectionWizard from './ConnectionWizard';
import NetworkAccessDialog from './NetworkAccessDialog';
import { ConnectionUnavailable } from './ConnectionSupport';
import { SqlNetworkStatus } from './SqlNetworkAccess';
import { defaultProgramForCredential, getProgramsForCredential } from './connectionPrograms';

// These are separate cards, not a joined accordion group. Keep their spacing
// and all four corners when the shared accordion theme resets expanded margins.
const sectionAccordionSx = (spacing = 3) => ({
  mt: spacing,
  '&&': { borderRadius: 1.5 },
  '&&.Mui-expanded': { mt: spacing, mb: 0 },
});

const fmtDateOnly = (value) => value ? formatDate(value, { dateStyle: 'medium' }) : '—';

function useCredentialState(kind, credential) {
  const [tick, update] = useState(0);
  const expires = Date.parse(credential.expires_at);
  useEffect(() => {
    const remaining = expires - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return undefined;
    const timer = setTimeout(() => update((value) => value + 1), Math.min(remaining + 10, 2147483647));
    return () => clearTimeout(timer);
  }, [expires, tick]);
  if (kind === 'sql' ? credential.status === 'revoked' : !credential.is_active) return 'revoked';
  return Number.isFinite(expires) && expires <= Date.now() ? 'expired' : 'issued';
}

function CredentialStatus({ kind, credential }) {
  const { t } = useTranslation();
  const state = useCredentialState(kind, credential);
  const [, refresh] = useState(0);
  const used = Number.isFinite(Date.parse(credential.last_used_at));
  useEffect(() => {
    if (!used || state !== 'issued') return undefined;
    const timer = setInterval(() => refresh((value) => value + 1), 60000);
    return () => clearInterval(timer);
  }, [used, state]);
  const key = state === 'revoked' ? (kind === 'sql' ? 'sqlRevoked' : 'revoked') : state === 'expired' ? 'expired' : 'issued';
  const label = state === 'issued' && used ? t('connections.usedAgo', { date: fmtRelative(credential.last_used_at) }) : t(`connections.${key}`);
  return <Chip size="small" title={used ? fmtDate(credential.last_used_at) : undefined} color={state === 'expired' ? 'warning' : state === 'revoked' ? 'default' : 'info'} label={label} />;
}

function CredentialIcon({ kind, credential }) {
  const tokens = useTokens();
  const Icon = kind === 'sql' ? StorageOutlinedIcon : credential.channels?.includes('mcp') ? SmartToyOutlinedIcon : CodeOutlinedIcon;
  return <Box sx={{ width: 40, height: 40, bgcolor: tokens.raised, borderRadius: 1.5, flexShrink: 0, display: 'grid', placeItems: 'center' }}><Icon sx={{ fontSize: 21 }} /></Box>;
}

function CredentialCard({ entry, onOpen, onNetwork, canEditNetwork, onRevoke, revokeDisabled = false }) {
  const { t } = useTranslation();
  const { kind, credential } = entry;
  const tokens = useTokens();
  const state = useCredentialState(kind, credential);
  const type = kind === 'sql' ? 'sqlType' : credential.channels?.includes('mcp') ? 'mcpType' : 'apiType';
  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', gap: 1.75, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' }, '& + &': { borderTop: `1px solid ${tokens.border}` } }}>
      <CredentialIcon kind={kind} credential={credential} />
      <Box sx={{ flex: 1, minWidth: 150 }}>
        <Typography component="h2" fontWeight={700} sx={{ overflowWrap: 'anywhere', fontSize: '0.95rem' }}>{credential.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t(`connections.${type}`)}{credential.kind === 'personal' && <> · {t('connections.personal')}</>}</Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {t('connections.createdAt', { date: fmtDateOnly(credential.created_at) })}{' · '}{credential.expires_at ? t('connections.expiryLabel', { date: fmtDateOnly(credential.expires_at) }) : t('connections.noExpiry')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
          <CredentialStatus kind={kind} credential={credential} />
        </Box>
        {state === 'revoked' && kind === 'sql' && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>{t('connections.sqlRevocationDelay')}</Typography>}
        {kind === 'sql' && entry.networkEnabled && <Box sx={{ mt: 1.5 }}><SqlNetworkStatus connection={credential} /></Box>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 6.75, sm: 0 }, flexWrap: 'wrap' }}>
        {canEditNetwork && kind === 'sql' && credential.status !== 'revoked' && (
          <Tooltip title={t('data.editSqlNetworkAccess')}><IconButton aria-label={t('data.editSqlNetworkAccess')} onClick={() => onNetwork(credential)}><SettingsEthernetIcon /></IconButton></Tooltip>
        )}
        <Button variant="outlined" size="small" onClick={() => onOpen(entry)} endIcon={<ArrowForwardIcon />}>{t('connections.openInstructions')}</Button>
        {onRevoke && <Button color="error" size="small" startIcon={<BlockIcon />} disabled={revokeDisabled} onClick={() => onRevoke(entry)}>{t('connections.disable')}</Button>}
      </Box>
    </Box>
  );
}

function PageHeader({ action }) {
  const { t } = useTranslation();
  return <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2.5, mb: 3 }}><Box><Typography component="h1" variant="h5" fontWeight={800}>{t('connections.title')}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('connections.subtitle')}</Typography></Box>{action}</Box>;
}

function EmptyState({ onCreate, disabled }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  return <>
    <Paper sx={{ py: { xs: 4, sm: 6 }, px: 3, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3.5, gap: 2 }}>
        <Box sx={{ width: 54, height: 54, bgcolor: tokens.accent, color: tokens.onAccent, borderRadius: 2, display: 'grid', placeItems: 'center' }}><StorageOutlinedIcon /></Box>
        <KeyOutlinedIcon sx={{ color: tokens.iconMuted }} />
        <Box sx={{ width: 54, height: 54, bgcolor: tokens.raised, borderRadius: 2, display: 'grid', placeItems: 'center' }}><BarChartOutlinedIcon /></Box>
      </Box>
      <Typography variant="h5" component="h2">{t('connections.emptyTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 430, mx: 'auto', mt: 1.5, mb: 3 }}>{t('connections.emptyBody')}</Typography>
      <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={onCreate} disabled={disabled}>{t('connections.create')}</Button>
      <Divider sx={{ my: 3.5 }} />
      <Stack direction="row" justifyContent="center" flexWrap="wrap" useFlexGap gap={2.5}>
        {['program', 'access', 'setup'].map((label, index) => <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 21, height: 21, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: tokens.raised, fontSize: 12 }}>{index + 1}</Box><Typography variant="caption" color="text.secondary">{t(`connections.${label}`)}</Typography></Box>)}
      </Stack>
    </Paper>
    <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ mt: 2 }}>{t('connections.emptyNote')}</Typography>
  </>;
}

function UsageContent({ usage }) {
  const { data: logs, isLoading } = useQueryLogs();
  return <Stack spacing={3}><StatCards usage={usage} /><UsageTrend usage={usage} /><RecentQueries logs={logs} isLoading={isLoading} /></Stack>;
}

function AdminConnections() {
  const { t } = useTranslation();
  const accessQuery = useBiConnectionsAccess();
  const access = accessQuery.data;
  const rowsQuery = useBiConnections({ pollNetwork: Boolean(access?.network_enabled), pollUsage: true });
  const keysQuery = useApiKeys({ pollUsage: true });
  const usageQuery = useBiUsage();
  const revokeSql = useRevokeBiConnection();
  const revokeKey = useDeleteApiKey();
  const [view, setView] = useState({ type: 'list' });
  const [hints, setHints] = useState({});
  const [notice, setNotice] = useState(null);
  const [networkConnection, setNetworkConnection] = useState(null);
  const [toRevoke, setToRevoke] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [showUsage, setShowUsage] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const rows = rowsQuery.data || [];
  const keys = keysQuery.data || [];
  const entries = [
    ...rows.filter((credential) => credential.status !== 'revoked').map((credential) => ({ kind: 'sql', credential, networkEnabled: Boolean(access?.network_enabled) })),
    ...keys.filter((credential) => credential.is_active).map((credential) => ({ kind: 'key', credential })),
  ].sort((a, b) => (Date.parse(b.credential.created_at) || 0) - (Date.parse(a.credential.created_at) || 0));
  const loading = rowsQuery.isLoading || keysQuery.isLoading;
  const loadError = rowsQuery.isError || keysQuery.isError;
  const selected = entries.find((entry) => entry.kind === view.kind && entry.credential.id === view.id);
  const selectedState = useCredentialState(selected?.kind || 'key', selected?.credential || { is_active: false });
  const hint = selected && hints[`${selected.kind}:${selected.credential.id}`];
  const programId = selected && (getProgramsForCredential(selected.kind, selected.credential).some((program) => program.id === hint) ? hint : defaultProgramForCredential(selected.kind, selected.credential));
  const start = () => { setNotice(null); if (access?.network_enabled) accessQuery.refetch(); setView({ type: 'create' }); };
  const open = (entry) => { setNotice(null); setView({ type: 'detail', kind: entry.kind, id: entry.credential.id }); };
  const onCreated = ({ kind, id, programId: chosen }) => setHints((current) => ({ ...current, [`${kind}:${id}`]: chosen }));
  const onExit = ({ completed, replacement }) => {
    setNotice(completed ? t(replacement ? 'connections.replacementNotice' : 'connections.createdNotice') : null);
    setView({ type: 'list' });
  };
  const confirmRevoke = async () => {
    if (!toRevoke) return;
    setActionError(null);
    try {
      if (toRevoke.kind === 'sql') await revokeSql.mutateAsync(toRevoke.credential.id);
      else await revokeKey.mutateAsync({ id: toRevoke.credential.id });
      setToRevoke(null);
      setView({ type: 'list' });
    } catch (error) {
      setActionError(extractApiError(error, t('connections.disableError')));
    } finally { revokeSql.reset(); revokeKey.reset(); }
  };
  const sqlCanChange = Boolean(access?.enabled) && !accessQuery.isError;
  const accessError = accessQuery.isError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button onClick={() => accessQuery.refetch()}>{t('connections.retry')}</Button>}>{t('connections.accessError')}</Alert>;

  if (view.type === 'create' || view.type === 'rotate' || view.type === 'replace') return <ConnectionWizard
    key={`${view.type}:${view.id || ''}`}
    access={accessQuery.isError ? null : access}
    accessError={accessQuery.isError}
    clientIp={accessQuery.isFetching || accessQuery.isError ? null : access?.client_ip}
    endpoints={usageQuery.data?.connection}
    onRetryAccess={() => accessQuery.refetch()}
    onRetryEndpoints={() => usageQuery.refetch()}
    onExit={onExit}
    onCreated={onCreated}
    rotationCredential={view.type === 'rotate' ? selected?.credential : null}
    replacementCredential={view.type === 'replace' ? selected?.credential : null}
    liveConnections={rows}
    initialProgramId={view.type === 'create' ? null : programId}
    replacement={view.type === 'replace'}
  />;

  return <Box>
    {view.type === 'detail' && selected ? <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => setView({ type: 'list' })} sx={{ mb: 2.5 }}>{t('connections.backToList')}</Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2 }}><CredentialIcon kind={selected.kind} credential={selected.credential} /><Typography variant="h5" component="h1" sx={{ overflowWrap: 'anywhere' }}>{selected.credential.name}</Typography></Box>
      <CredentialStatus kind={selected.kind} credential={selected.credential} />
      {accessError}
      <Paper sx={{ mt: 3, p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>{t('connections.instructions')}</Typography>
        <ConnectionInstructions kind={selected.kind} credential={selected.credential} programId={programId} endpoints={usageQuery.data?.connection} />
        {selected.kind === 'sql' && access?.network_enabled && <Box sx={{ mt: 3 }}><SqlNetworkStatus connection={selected.credential} /></Box>}
      </Paper>
      <Accordion sx={sectionAccordionSx(2)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('connections.manage')}</AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" useFlexGap gap={1.5} flexWrap="wrap">
            {selected.kind === 'sql' && selected.credential.kind !== 'personal' && selectedState === 'issued' && <Button variant="outlined" disabled={!sqlCanChange} onClick={() => setView({ type: 'rotate', kind: selected.kind, id: selected.credential.id })}>{t('connections.newPassword')}</Button>}
            {selected.kind === 'key' && selected.credential.is_active && <Button variant="outlined" onClick={() => setView({ type: 'replace', kind: selected.kind, id: selected.credential.id })}>{t('connections.replaceKey')}</Button>}
            <Button color="error" variant="outlined" disabled={selected.kind === 'sql' && !sqlCanChange} onClick={() => { setActionError(null); setToRevoke(selected); }}>{t('connections.disable')}</Button>
          </Stack>
          {selected.kind === 'sql' && access && !access.enabled && <ConnectionUnavailable type="directSql" sx={{ mt: 2 }} />}
        </AccordionDetails>
      </Accordion>
    </> : <>
      <PageHeader action={entries.length ? <Button variant="contained" startIcon={<AddIcon />} disabled={loading || loadError} onClick={start}>{t('connections.create')}</Button> : null} />
      {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
      {accessError}
      {rowsQuery.isError && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => rowsQuery.refetch()}>{t('connections.retry')}</Button>}>{t('connections.listError')}</Alert>}
      {keysQuery.isError && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => keysQuery.refetch()}>{t('connections.retry')}</Button>}>{t('connections.keysError')}</Alert>}
      {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} aria-label={t('connections.loading')} /></Box> : entries.length ? <Paper>
        {entries.map((entry) => <CredentialCard key={`${entry.kind}:${entry.credential.id}`} entry={entry} onOpen={open} onNetwork={(entry) => { setNetworkConnection(entry); accessQuery.refetch(); }} canEditNetwork={sqlCanChange && Boolean(access?.network_enabled)} onRevoke={(selected) => { setActionError(null); setToRevoke(selected); }} revokeDisabled={entry.kind === 'sql' && !sqlCanChange} />)}
      </Paper> : !loadError && <EmptyState onCreate={start} />}
      {keys.length > 0 && <Accordion sx={sectionAccordionSx()} disableGutters expanded={showKeys} onChange={(_event, value) => setShowKeys(value)} TransitionProps={{ unmountOnExit: true }}><AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('connections.keyAdvanced')}</AccordionSummary><AccordionDetails>{showKeys && <TokensPanel />}</AccordionDetails></Accordion>}
      <Accordion sx={sectionAccordionSx()} disableGutters expanded={showUsage} onChange={(_event, value) => setShowUsage(value)} TransitionProps={{ unmountOnExit: true }}><AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('connections.analytics')}</AccordionSummary><AccordionDetails>{showUsage && (usageQuery.isError ? <Alert severity="warning">{t('connections.usageError')}</Alert> : <UsageContent usage={usageQuery.data} />)}</AccordionDetails></Accordion>
    </>}
    {networkConnection && sqlCanChange && access?.network_enabled && <NetworkAccessDialog key={networkConnection.id} connection={networkConnection} clientIp={accessQuery.isFetching || accessQuery.isError ? null : access?.client_ip} onClose={() => setNetworkConnection(null)} onReload={async (id) => { const result = await rowsQuery.refetch({ throwOnError: true }); return result.data?.find((row) => row.id === id); }} />}
    <ConfirmActionDialog open={Boolean(toRevoke)} title={t('connections.disableTitle')} confirmWord={t('connections.disableWord')} confirmLabel={t('connections.disable')} loading={revokeSql.isPending || revokeKey.isPending} error={actionError} blocked={toRevoke?.kind === 'sql' && !sqlCanChange} onConfirm={confirmRevoke} onClose={() => setToRevoke(null)}>
      <Typography fontWeight={700} sx={{ mb: 1 }}>{toRevoke?.credential.name}</Typography>
      {toRevoke?.kind === 'sql' && !sqlCanChange ? <ConnectionUnavailable type="directSql" /> : <Typography variant="body2">{t('connections.disableBody')}</Typography>}
    </ConfirmActionDialog>
  </Box>;
}

function PersonalConnections() {
  const { t } = useTranslation();
  const accessQuery = useBiConnectionsAccess();
  const rowsQuery = useBiConnections({ pollNetwork: Boolean(accessQuery.data?.network_enabled), pollUsage: true });
  const [selectedId, setSelectedId] = useState(null);
  const rows = (rowsQuery.data || []).filter((row) => row.kind === 'personal' && row.status !== 'revoked');
  const selected = rows.find((row) => row.id === selectedId);
  return <Box>
    <PageHeader />
    {rowsQuery.isLoading ? <CircularProgress aria-label={t('connections.loading')} /> : rowsQuery.isError ? <Alert severity="error" action={<Button onClick={() => rowsQuery.refetch()}>{t('connections.retry')}</Button>}>{t('connections.listError')}</Alert> : rows.length ? <>
      <Paper>{rows.map((credential) => <CredentialCard key={credential.id} entry={{ kind: 'sql', credential, networkEnabled: Boolean(accessQuery.data?.network_enabled) }} onOpen={() => setSelectedId(credential.id)} />)}</Paper>
      {selected && <Paper sx={{ mt: 3, p: 3 }}><Typography variant="h6" sx={{ mb: 2 }}>{t('connections.instructions')}</Typography><ConnectionInstructions kind="sql" credential={selected} /></Paper>}
    </> : <Paper sx={{ p: 4, textAlign: 'center' }}><LockOutlinedIcon sx={{ mb: 2, fontSize: 30 }} /><Typography variant="h6">{t('connections.memberTitle')}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('connections.memberBody')}</Typography></Paper>}
  </Box>;
}

/** Credential-bearing children remount on a principal or privilege change. */
export default function ConnectionsPage() {
  const { role, isAdmin } = useRole();
  const principal = useAuthStore((state) => `${state.user?.id ?? ''}:${state.activeMembership?.tenant?.public_id ?? ''}`);
  return isAdmin ? <AdminConnections key={`${principal}:${role}`} /> : <PersonalConnections key={`${principal}:${role}`} />;
}
